import { useEffect, useState } from 'react';
import { getStoredPublishedActiveTemplate, normalizeTemplateForReader } from './TemplateStorage.jsx';
import { getDomainThemeDefaults, normalizeTemplateTheme } from './TemplateTheme.jsx';

const FLEX_OFFICE_DOMAIN = 'FLEX_OFFICE';
const defaultFlexOfficeTheme = getDomainThemeDefaults(FLEX_OFFICE_DOMAIN);

const darkFlexOfficeDefaults = {
  background: '#0f172a',
  surface: '#111827',
  surfaceSoft: '#1e293b',
  surfaceMuted: '#334155',
  color: '#f8fafc',
  mutedColor: '#cbd5e1',
  subtleColor: '#94a3b8',
  borderColor: 'rgba(148, 163, 184, 0.22)',
  borderStrong: 'rgba(148, 163, 184, 0.36)',
  shadow: '0 22px 70px rgba(0, 0, 0, 0.42)'
};

const lightFlexOfficeDefaults = {
  surfaceSoft: '#f1f5f9',
  surfaceMuted: '#e2e8f0',
  subtleColor: '#64748b',
  borderStrong: '#cbd5e1',
  shadow: '0 18px 45px rgba(15, 23, 42, 0.12)'
};

const pick = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const normalizeHex = (value) => {
  const color = String(value || '').trim();
  if (!color.startsWith('#')) return null;
  const raw = color.slice(1);
  if (raw.length === 3) {
    return raw.split('').map((char) => char + char).join('');
  }
  if (raw.length === 6) return raw;
  return null;
};

export const flexOfficeAlpha = (color, opacity = 0.16) => {
  const hex = normalizeHex(color);
  if (!hex) return `rgba(37, 99, 235, ${opacity})`;

  const intValue = parseInt(hex, 16);
  const red = (intValue >> 16) & 255;
  const green = (intValue >> 8) & 255;
  const blue = intValue & 255;
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const shadeHex = (color, amount = -24) => {
  const hex = normalizeHex(color);
  if (!hex) return color;

  const clamp = (value) => Math.max(0, Math.min(255, value));
  const intValue = parseInt(hex, 16);
  const red = clamp(((intValue >> 16) & 255) + amount);
  const green = clamp(((intValue >> 8) & 255) + amount);
  const blue = clamp((intValue & 255) + amount);
  return `#${((1 << 24) + (red << 16) + (green << 8) + blue).toString(16).slice(1)}`;
};

const activeStoredFlexOfficeTemplate = () => {
  return getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: FLEX_OFFICE_DOMAIN
  });
};

export const getActiveFlexOfficeTemplate = () => {
  const sourceTemplate = activeStoredFlexOfficeTemplate();
  return sourceTemplate ? normalizeTemplateForReader(sourceTemplate, FLEX_OFFICE_DOMAIN) : null;
};

export const getActiveFlexOfficeTheme = () => {
  const template = getActiveFlexOfficeTemplate();
  const normalizedTheme = template ? normalizeTemplateTheme(template, FLEX_OFFICE_DOMAIN) : defaultFlexOfficeTheme;
  const primary = pick(normalizedTheme.primary, defaultFlexOfficeTheme.primary);
  const secondary = pick(normalizedTheme.secondary, defaultFlexOfficeTheme.secondary);
  const accent = pick(normalizedTheme.accent, normalizedTheme.primaryDark, defaultFlexOfficeTheme.accent, primary);
  const primaryDark = pick(normalizedTheme.primaryDark, shadeHex(primary, -24));

  return {
    ...defaultFlexOfficeTheme,
    ...normalizedTheme,
    primary,
    primaryColor: primary,
    primaryDark,
    primaryHover: primaryDark,
    primaryLight: flexOfficeAlpha(primary, 0.16),
    secondary,
    secondaryColor: secondary,
    secondaryDark: pick(normalizedTheme.secondaryDark, shadeHex(secondary, -18)),
    secondaryLight: flexOfficeAlpha(secondary, 0.14),
    accent,
    accentColor: accent,
    background: pick(normalizedTheme.background, defaultFlexOfficeTheme.background),
    surface: pick(normalizedTheme.surface, defaultFlexOfficeTheme.surface),
    color: pick(normalizedTheme.color, defaultFlexOfficeTheme.color),
    text: pick(normalizedTheme.color, defaultFlexOfficeTheme.color),
    mutedColor: pick(normalizedTheme.mutedColor, defaultFlexOfficeTheme.mutedColor),
    muted: pick(normalizedTheme.mutedColor, defaultFlexOfficeTheme.mutedColor),
    borderColor: pick(normalizedTheme.borderColor, defaultFlexOfficeTheme.borderColor),
    border: pick(normalizedTheme.borderColor, defaultFlexOfficeTheme.borderColor),
    fontFamily: normalizedTheme.fontFamily || defaultFlexOfficeTheme.fontFamily,
    headingFontFamily: normalizedTheme.headingFontFamily || normalizedTheme.fontFamily || defaultFlexOfficeTheme.headingFontFamily,
    buttonRadius: normalizedTheme.buttonRadius || defaultFlexOfficeTheme.buttonRadius,
    cardRadius: normalizedTheme.cardRadius || defaultFlexOfficeTheme.cardRadius,
    cardShadow: normalizedTheme.cardShadow || defaultFlexOfficeTheme.cardShadow,
    inputRadius: normalizedTheme.inputRadius || defaultFlexOfficeTheme.inputRadius,
    footerBackground: normalizedTheme.footerBackground || defaultFlexOfficeTheme.footerBackground,
    gradient: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
    primaryGradient: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
    railGradient: `linear-gradient(90deg, ${primary} 0%, ${accent} 100%)`
  };
};

export const useActiveFlexOfficeTheme = () => {
  const [activeTheme, setActiveTheme] = useState(() => getActiveFlexOfficeTheme());

  useEffect(() => {
    const refresh = () => setActiveTheme(getActiveFlexOfficeTheme());
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  return activeTheme;
};

export const getFlexOfficeThemeCssVariables = (theme = getActiveFlexOfficeTheme(), mode = 'light') => {
  const darkMode = mode === 'dark';
  const surfaces = darkMode
    ? darkFlexOfficeDefaults
    : {
        background: theme.background,
        surface: theme.surface,
        surfaceSoft: lightFlexOfficeDefaults.surfaceSoft,
        surfaceMuted: lightFlexOfficeDefaults.surfaceMuted,
        color: theme.color,
        mutedColor: theme.mutedColor,
        subtleColor: lightFlexOfficeDefaults.subtleColor,
        borderColor: theme.borderColor,
        borderStrong: lightFlexOfficeDefaults.borderStrong,
        shadow: theme.cardShadow || lightFlexOfficeDefaults.shadow
      };

  return {
    '--flex-primary': theme.primary,
    '--flex-primary-hover': theme.primaryHover,
    '--flex-primary-dark': theme.primaryDark,
    '--flex-primary-soft': flexOfficeAlpha(theme.primary, 0.16),
    '--flex-primary-muted': flexOfficeAlpha(theme.primary, 0.3),
    '--flex-secondary': theme.secondary,
    '--flex-secondary-dark': theme.secondaryDark,
    '--flex-secondary-soft': flexOfficeAlpha(theme.secondary, 0.12),
    '--flex-accent': theme.accent,
    '--flex-accent-soft': flexOfficeAlpha(theme.accent, 0.14),
    '--flex-bg': surfaces.background,
    '--flex-bg-elevated': surfaces.surface,
    '--flex-surface': surfaces.surface,
    '--flex-surface-soft': surfaces.surfaceSoft,
    '--flex-surface-muted': surfaces.surfaceMuted,
    '--flex-border': surfaces.borderColor,
    '--flex-border-strong': surfaces.borderStrong,
    '--flex-text': surfaces.color,
    '--flex-text-muted': surfaces.mutedColor,
    '--flex-text-subtle': surfaces.subtleColor,
    '--flex-shadow': surfaces.shadow,
    '--flex-font': theme.fontFamily,
    '--flex-heading-font': theme.headingFontFamily,
    '--flex-button-radius': theme.buttonRadius,
    '--flex-card-radius': theme.cardRadius,
    '--flex-card-shadow': theme.cardShadow,
    '--flex-input-radius': theme.inputRadius,
    '--flex-gradient': theme.gradient,
    '--flex-primary-gradient': theme.primaryGradient,
    '--flex-rail-gradient': theme.railGradient,
    '--flex-footer-bg': darkMode ? theme.footerBackground || defaultFlexOfficeTheme.footerBackground : theme.footerBackground
  };
};

export const flexOfficeButtonSx = (theme, variant = 'primary') => {
  const isOutline = variant === 'outline';
  return {
    borderRadius: theme.buttonRadius || '12px',
    fontFamily: theme.fontFamily,
    fontWeight: 700,
    textTransform: 'none',
    ...(isOutline
      ? {
          color: theme.primary,
          borderColor: theme.primary,
          '&:hover': {
            borderColor: theme.primaryHover,
            backgroundColor: flexOfficeAlpha(theme.primary, 0.1)
          }
        }
      : {
          color: '#ffffff',
          background: theme.primaryGradient,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.primaryHover} 0%, ${theme.primary} 100%)`
          }
        })
  };
};
